import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getDiagnosticsAnalyticsList, getDiagnosticsAnalyticsListAction } from "../../../Api/BmsFaultsApi";
import { formatDateForAPI } from "../../../Helper/Analytics/AnalyticsHelper";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { useTranslation } from "react-i18next";
import Loader from "../../../Helper/Loaders/Loader";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../Helper/DigitalTwin/DataFile";
import { CHART_CONFIG, CHART_TYPES } from "./chartConfig";
import { usePagesContent, useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import WarningIcon from "@mui/icons-material/Warning";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { useBmsMetaInfoFields, useBmsMetaReady, useBmsTableConfig } from "./useBmsTableConfig";

const MostCriticalVehicles = ({ startDate, endDate, globalFilters = {} }) => {
  const { t } = useTranslation();
  const pagesContent = usePagesContent();
  const navigate = useNavigate();
  const {baseEntityType, baseEntityTypeIDLable, baseEntityTypeLable, baseEntityTypePluralLable} = useBaseEntityInfo();

  // Use SEVERITY config from chartConfig.js
  const config = CHART_CONFIG[CHART_TYPES.MOST_CRITICAL_VEHICLES];

  // Columns come from the bmsAlertV2 org meta when configured, chartConfig otherwise.
  const metaReady = useBmsMetaReady();
  const resolveTable = useBmsTableConfig();
  const resolveMetaInfo = useBmsMetaInfoFields();
  // Memoised: both feed dependency arrays below, and a fresh object or array on
  // every render would rebuild the columns and refetch continuously.
  const resolvedTable = useMemo(
    () => resolveTable("mostCriticalVehiclesTable", config),
    [resolveTable, config]
  );
  const metaInfoFields = useMemo(
    () => resolveMetaInfo(config.metaInfo, "mostCriticalVehiclesTable", config),
    [resolveMetaInfo, config]
  );

  const defaultSortBy = [
    { "column": "critical", "direction": "DESC" },
    { "column": "high severity", "direction": "DESC" },
    { "column": "warning", "direction": "DESC" },
  ];

  const RECORD_LIMIT = 20;

  const [loading, setLoading] = useState(true);
  const [vehicleData, setVehicleData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState([]);

  // Column management
  const [initialColumns, setInitialColumns] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);

  // Selection management
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Navigate to battery details
  const onBatteryIDClick = React.useCallback((record) => {
    const params = {
      batteryID: record.id,
      deviceID: record.deviceID || record.busNumber,
      batterySearchedGlobal: "false",
      prevLink: "/",
      navigateFromTab: true,
      tabValue: "alerts",
    };
    encryptTheParams(params, navigate, true);
  }, [navigate]);

  // Custom renderers for columns
  const customRenderers = React.useMemo(() => ({
    "device-id-link": (record, config) => {
      return (
        <DeviceIDLink
          record={record}
          config={config}
          onTextClick={() => onBatteryIDClick(record)}
        />
      );
    },
  }), [onBatteryIDClick]);

  // Setup table columns
  const setupTableColumns = React.useCallback(() => {
    const availableColumns = getColumnsConfig({
      tableColumns: resolvedTable.tableColumnsConfig,
      tableColumnOrder: resolvedTable.tableColumnOrder,
      customRenderers: customRenderers,
      t,
    });

    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys = defaultSelected.map((col) => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((col) => col.headerLabel));
  }, [resolvedTable, customRenderers, t]);

  // Fetch vehicle data using same API pattern as ChartDetailsView
  useEffect(() => {
    const fetchVehicleData = async () => {
      // Wait for the org meta to settle before the first fetch. It decides which
      // metaInfo this request asks for, so a fetch that beats it asks for nothing
      // and paints em dashes before repainting.
      if (!metaReady) return;
      if (!startDate || !endDate) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Prepare global filters with hierarchical location, customer scope and
        // alert categories. fleets has to be forwarded or this table ignores the
        // page-level customer selection; an empty one sends no fleetIDs header.
        const globalFilterParams = {
          hierarchicalLocation: globalFilters?.hierarchicalLocation,
          fleets: globalFilters?.fleets,
          // See the hook: the originLocations header is derived from these two.
          onboardingLocations: globalFilters?.onboardingLocations,
          allOnboardingLocations: globalFilters?.allOnboardingLocations,
          alertCategories: globalFilters?.alertCategories,
          operationalStatus: globalFilters?.operationalStatus
        };

        // Prepare API parameters - same as SEVERITY chart type in ChartDetailsView
        const params = {
          startDate: formatDateForAPI(startDate),
          endDate: formatDateForAPI(endDate),
          select: ['GeneratedFaultsCount'],
          filters: [],
          groupBy: ['batteryID', 'FaultSeverityName'],
          granularity: null,
          metaInfo: metaInfoFields,
          pageNum: pageNumber,
          pageCount: rowsPerPage,
          skipPagination: false,
          limit: RECORD_LIMIT,
        };

        params.sortBy = JSON.stringify(defaultSortBy);

        if (sortBy.length > 0) {
          params.sortBy = JSON.stringify([...defaultSortBy, ...sortBy]);
        }

        const response = await getDiagnosticsAnalyticsList(globalFilterParams, params);
        console.log("response", { response, params, sortBy });
        const listData = response?.response?.data || [];
        const responseTotal = response?.response?.total?.total || listData.length;

        // Transform API response to table format - same as ChartDetailsView
        const transformedData = listData.map((item) => {
          const row = {
            id: item.xLabel,
            key: item.xLabel,
            licensePlateNumber: item.xLabel || '',
            ...item.metaInfo,
          };

          // Add metrics from metricsArray
          if (item.metricsArray && Array.isArray(item.metricsArray)) {
            item.metricsArray.forEach(metric => {
              const severityType = (metric.type || '').toLowerCase();
              if (severityType === 'warning' || severityType === 'warn') {
                row['warning'] = metric.value || 0;
              } else if (severityType === 'high severity' || severityType === 'high' || severityType === 'highseverity') {
                row['high severity'] = metric.value || 0;
              } else if (severityType === 'critical' || severityType === 'crit') {
                row['critical'] = metric.value || 0;
              }
            });
          }

          // Ensure all severity fields exist
          if (!row.warning) row.warning = 0;
          if (!row['high severity']) row['high severity'] = 0;
          if (!row['critical']) row['critical'] = 0;

          return row;
        });

        setVehicleData(transformedData);
        setTotal(responseTotal);
      } catch (error) {
        console.error("Error fetching most critical vehicles:", error);
        setVehicleData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleData();
  }, [startDate, endDate, globalFilters, pageNumber, rowsPerPage, sortBy, metaInfoFields, metaReady]);

  // Reset pagination and sorting when filters/date changes
  useEffect(() => {
    setPageNumber(1);
    // Through an updater that keeps the previous array when already empty. A
    // fresh [] is a new identity and sortBy is a fetch dependency, so an
    // unconditional clear fired a second identical request on mount and on
    // every filter change. Same guard as ChartDrillDownTable.
    setSortBy((prev) => (prev.length === 0 ? prev : []));
  }, [startDate, endDate, globalFilters]);

  // Initialize columns. Depends on setupTableColumns rather than running once at
  // mount, because the org meta arrives after the first render: with an empty
  // dependency list the table kept the chartConfig fallback columns for the life
  // of the page while the fetch below asked the API for the configured ones.
  useEffect(() => {
    setupTableColumns();
  }, [setupTableColumns]);

  // Helper function to build filters for API calls
  const buildFilters = () => {
    return {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
      select: ['GeneratedFaultsCount'],
      groupBy: ['batteryID', 'FaultSeverityName'],
      granularity: null,
      metaInfo: metaInfoFields,
      columns: tableColumns,
      columnOrder,
      resourceIDs: Array.from(totalSelectedRowKeys),
      selectAll: isSelectAll,
      filters: [],
      limit: RECORD_LIMIT,
      ...(sortBy.length > 0 ? { sortBy: JSON.stringify([...defaultSortBy, ...sortBy]) } : { sortBy: JSON.stringify(defaultSortBy) }),
    };
  };

  // Helper function to build global filters. Used by the CSV and mail actions,
  // which must be scoped exactly like the on-screen list above.
  const buildGlobalFilters = () => ({
    hierarchicalLocation: globalFilters?.hierarchicalLocation,
    fleets: globalFilters?.fleets,
    onboardingLocations: globalFilters?.onboardingLocations,
    allOnboardingLocations: globalFilters?.allOnboardingLocations,
    alertCategories: globalFilters?.alertCategories,
    operationalStatus: globalFilters?.operationalStatus
  });

  // Helper function to call API with action type
  const callDiagnosticsApi = async (actionType, additionalParams = {}) => {
    const filters = buildFilters();
    const globalFilterParams = buildGlobalFilters();
    const res = await getDiagnosticsAnalyticsListAction(globalFilterParams, {
      actionType,
      ...additionalParams,
      ...filters,
    });

    if (res && res.responseStatus.code === 200) {
      return res.response.data;
    }
    return [];
  };

  // CSV Download
  const getCSVData = async () => {
    return await callDiagnosticsApi("download");
  };

  // CSV Filename
  const getCSVFileName = () => {
    const formattedStartDate = new Date(startDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const formattedEndDate = new Date(endDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return `Most_Critical_${baseEntityTypePluralLable}_${formattedStartDate}-${formattedEndDate}.csv`;
  };

  // Send Email
  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    return await callDiagnosticsApi("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject: subject,
      body: body,
    });
  };

  // Row Selection
  const onSelectChange = (record, selected) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }
    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((row) => row?.id)) : new Set()
    );
  };

  const getTotalRowSelection = () => Array.from(totalSelectedRowKeys);

  const getTotalRowSelectionCount = () =>
    isSelectAll ? total : totalSelectedRowKeys.size;

  const getCurrentPageSelection = () => Array.from(totalSelectedRowKeys);

  // Column Management
  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filteredColumns = initialColumns.filter((columnConfig) =>
      newSelectedKeys.includes(columnConfig.dataIndex)
    );

    setTableColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filteredColumns.map((col) => col.headerLabel));
  };

  // Pagination
  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  // Clear Selection
  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  // Sorting
  const handleSortChange = (key, order) => {
    const direction = order === "asc" ? "ASC" : "DESC";


    const newSortBy = [{ column: key, direction }];
    setSortBy(newSortBy);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  // Get current sorting state for initialSorting prop
  const getCurrentSorting = () => {
    if (sortBy && sortBy.length > 0) {
      const firstSort = sortBy[sortBy.length - 1];
      return {
        sortBy: firstSort.column,
        orderBy: firstSort.direction.toLowerCase()
      };
    }
    return {};
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Table Section */}
      <Box
        sx={{
          borderRadius: "8px",
          borderLeft: "3px solid #B50500",
          backgroundColor: "#FFF1F1",
          padding: "24px 32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mb: 2,
          }}
        >
          <Box>
            <PrimaryTooltip
              title={`${baseEntityTypePluralLable} with the highest number of critical faults are shown first, followed by high-severity faults and warnings`}
              placement="bottom"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningIcon sx={{ color: "#B50500", fontSize: 20 }} />
                <Typography variant="fw600s18lh24cB50500">
                  Most Critical {baseEntityTypePluralLable} in your Fleet
                </Typography>
              </Box>
            </PrimaryTooltip>
          </Box>
          <Typography
            variant="fw400s14lh24c3D4B5A"
            sx={{ color: "#B50500 !important" }}
          >
            Top 20 {baseEntityTypePluralLable.toLowerCase()} with the highest number of critical, high-severity, and warning faults
          </Typography>
        </Box>

        {/* Table */}
        <GenericDraggableTableView
          title={`${baseEntityTypeLable} list`}
          showPrimaryButton={false}
          count={total}
          tableColumns={tableColumns}
          selectedColumns={selectedColumnKeys}
          tableData={vehicleData}
          availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
          initialColumns={initialColumns}
          applySelectedColumns={applySelectedColumns}
          defaultSelectedColumns={defaultSelectedColumnKeys}
          isLoading={loading}
          pageNumber={pageNumber}
          rowsPerPage={rowsPerPage}
          total={total}
          disablePagination={false}
          disableSearch={true}
          disableFiltersSettings={true}
          getCSVData={getCSVData}
          getCSVFileName={getCSVFileName}
          onSendEmail={onSendEmail}
          columnOrder={columnOrder}
          onColumnOrderChange={onColumnOrderChange}
          applySort={handleSortChange}
          onPageChange={handlePageChange}
          initialSorting={getCurrentSorting()}
          onClearSelection={handleClearSelection}
          emptyMessage={`No critical ${baseEntityTypePluralLable.toLowerCase()} found`}
          containerStyles={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            border: "1px solid #E6E6E6",
            padding: "12px 24px",
            overflow: "visible",
            maxWidth: 'calc(100vw - 220px)'
          }}
          getTotalRowSelection={getTotalRowSelection}
          getTotalRowSelectionCount={getTotalRowSelectionCount}
          rowSelection={{
            selectedRowKeys: getCurrentPageSelection(),
            onSelect: onSelectChange,
            onSelectAll: onSelectAllChange,
          }}
          maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
        />
      </Box>
    </Box>
  );
};

export default MostCriticalVehicles;
